import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bubble, Icon } from 'tea-component';
import { useIntl } from 'umi';

import MultipleCascader from '@/pages/datalab/components/MultipleCascader';
import { isSamePath } from '@/pages/datalab/components/MultipleCascader/Panel/panelUtils';
import type {
  ICascaderChangeContextCheck,
  ICascaderMenuOption,
  TCascaderChangeContext,
  TStringPath,
} from '@/pages/datalab/components/MultipleCascader/types';
import { buildAbTestDisplayGroups } from './hooks/abTestCascaderValueRenderer';
import useAbTestCascaderOnChange from './hooks/useAbTestCascaderOnChange';
import useAbTestCascaderDisabledOptions from './hooks/useAbTestCascaderDisabledOptions';

import './index.scss';

interface IABTestCascaderProps {
  data: ICascaderMenuOption[];
  value?: TStringPath[]; // 受控值：每一项是一条从第一级到叶子的路径
  onChange?: (value: TStringPath[], context: TCascaderChangeContext) => void;
  loading?: boolean;
  error?: boolean | string;
}

const ABTestCascader: React.FC<IABTestCascaderProps> = (props) => {
  const { data, value, onChange, loading, error } = props;
  const { formatMessage } = useIntl();
  const [innerValue, setInnerValue] = useState<TStringPath[]>(() => value || []);
  const columnTitles = [
    formatMessage({
      id: 'dbl_ab_experiment_abtest_cascader_experiment_layer',
    }),
    formatMessage({
      id: 'dbl_ab_experiment_abtest_cascader_experiment_name',
    }),
    formatMessage({
      id: 'dbl_ab_experiment_abtest_cascader_experiment_group',
    }),
  ];

  useEffect(() => {
    if (Array.isArray(value)) {
      setInnerValue(value);
    }
  }, [value]);

  // 优先使用外部传入的数据，未传入时回退到本地 mock 数据
  const sourceData =
    Array.isArray(data) ? data : [];

  // 基于当前选中值计算同一级下二级的禁用态
  const normalizedData = useAbTestCascaderDisabledOptions(
    sourceData,
    innerValue,
  );

  // 使用组合 Hook 统一处理“二级单选 + 默认展开”逻辑
  const handleChange = useAbTestCascaderOnChange(
    sourceData,
    true,
    value,
    onChange,
    setInnerValue,
  );
  const displayGroups = useMemo(
    () => buildAbTestDisplayGroups(sourceData, innerValue),
    [sourceData, innerValue],
  );

  const bubbleContent = displayGroups.length ? (
    <>
      {displayGroups.map((group) => (
        <div
          key={`${group.level1Label}-${group.level2Label}`}
        >
          {group.level1Label}
          :
          {group.level2Label}
          {group.level3Labels.length
            ? `(${group.level3Labels.join(', ')})`
            : null}
        </div>
      ))}
    </>
  ) : null;

  const handleRemoveValueTag = useCallback(
    (pathToRemove: TStringPath, chain: ICascaderMenuOption[]) => {
      const nextValue = innerValue.filter((p) => !isSamePath(p, pathToRemove));
      const option = chain[chain.length - 1];
      const context: ICascaderChangeContextCheck = {
        type: 'uncheck',
        path: pathToRemove,
        option,
        optionChain: chain,
        value: nextValue,
        originContext: undefined,
      };
      handleChange(nextValue, context);
    },
    [handleChange, innerValue],
  );

  const internalValueRender = useCallback(
    (selectedChains: ICascaderMenuOption[][]) => {
      const level3Chains = selectedChains.filter((chain) => chain.length >= 3);

      if (!level3Chains.length) {
        return null;
      }

      return (
        <div className="dbl-abtest-cascader-value-tags">
          {level3Chains.map((chain) => {
            const path = chain.map((node) => node.value);
            const pathKey = path.join('/');
            const formatNodeText = (node?: ICascaderMenuOption): string => {
              if (!node) return '';
              if (
                typeof node.label === 'string' ||
                typeof node.label === 'number'
              ) {
                return String(node.label);
              }
              return node.value || '';
            };
            const level1Label = formatNodeText(chain[0]);
            const level2Label = formatNodeText(chain[1]);
            const level3Label = formatNodeText(chain[chain.length - 1]);
            const tagLabel = [level1Label, level2Label, level3Label]
              .filter(Boolean)
              .join(':');

            return (
              <div
                className="tea-tag dbl-abtest-cascader-value-tag"
                key={pathKey}
              >
                <span className="dbl-abtest-cascader-value-tag__text">
                  {tagLabel}
                </span>
                <Icon
                  type="close"
                  onClick={(event): void => {
                    event.stopPropagation();
                    handleRemoveValueTag(path, chain);
                  }}
                />
              </div>
            );
          })}
        </div>
      );
    },
    [handleRemoveValueTag],
  );

  return (
    <Bubble
      content={bubbleContent}
      placementOffset={[0, 20]}
      overlayClassName='dbl-abtest-cascader-bubble-overlay'
    >
      <MultipleCascader
        className="dbl-abtest-cascader"
        data={normalizedData}
        value={innerValue}
        onChange={handleChange}
        valueRender={internalValueRender}
        columnTitles={columnTitles}
        placeholder={formatMessage({
          id: 'dbl_ab_experiment_abtest_cascader_placeholder',
        })}
        searchPlaceholder={formatMessage({
          id: 'dbl_ab_experiment_abtest_cascader_search_placeholder',
        })}
        loading={loading}
        error={error}
        overlayClassName="dbl-abtest-cascader-overlay"
      />
    </Bubble>
  );
};

export default ABTestCascader;

